import { getDoc, setDoc, serverTimestamp, doc } from 'firebase/firestore';
import { geminiGenerateJson } from '../../geminiService';
import { schoolSchemaRef } from './schoolSchemaPaths';
import { getEvidenceAnalysisJob } from './evidenceAnalysisJob';
import { db } from '../../../firebase-config';
import { AI_PIPELINE_SCHEMA_V2 } from '../../../constants/aiPipelineModes';

/** Keep under functions/geminiClient MAX_PROMPT_CHARS (120000). */
const PROMPT_BUDGET = 95000;

function clipText(value, max) {
  const s = String(value ?? '');
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1))}…`;
}

function clipJson(value, maxChars) {
  const raw = JSON.stringify(value);
  if (raw.length <= maxChars) return raw;
  return `${raw.slice(0, Math.max(0, maxChars - 20))}…[truncated]"`;
}

function compactStations(stations, narrativeMax) {
  const out = {};
  Object.entries(stations || {}).forEach(([id, s]) => {
    out[id] = {
      selectedLevel: s?.selectedLevel,
      narrative: clipText(s?.narrative || '', narrativeMax),
    };
  });
  return out;
}

function compactFields(fields, valueMax, maxFields) {
  return Object.entries(fields || {})
    .slice(0, maxFields)
    .map(([id, e]) => {
      let value = e?.value;
      if (typeof value === 'string') value = clipText(value, valueMax);
      else if (value != null && typeof value === 'object') {
        const j = JSON.stringify(value);
        value = j.length <= valueMax ? value : clipText(j, valueMax);
      }
      return {
        id,
        value,
        source: e?.provenance?.source || 'unknown',
        fileName: e?.provenance?.fileName || null,
      };
    });
}

function compactFiles(files, detailMax, maxFiles) {
  const list = Array.isArray(files) ? files : [];
  const preferred = [
    ...list.filter((f) => f.status === 'extracted' || f.status === 'done'),
    ...list.filter((f) => f.status === 'failed'),
  ];
  const rest = list.filter(
    (f) => f.status !== 'extracted' && f.status !== 'done' && f.status !== 'failed'
  );
  const ordered = [...preferred, ...rest].slice(0, maxFiles);
  return ordered.map((f) => ({
    name: f.name,
    status: f.status,
    kind: f.kind,
    detail: clipText(f.detail || '', detailMax),
    quality: f.quality
      ? {
          score: f.quality.score ?? null,
          worthiness: f.quality.worthiness || null,
          summary: clipText(f.quality.summary || '', detailMax),
        }
      : null,
    tags: Array.isArray(f.tags) ? f.tags.slice(0, 6) : [],
  }));
}

function buildMapperPrompt({ stationPayload, fieldSummaries, fileSummaries }) {
  return `
SYSTEM: IFIP schema_v2 soft-evidence and claims mapper.
You extract discrete SEF claims and label what is soft (narrative-only) vs file/schema-backed.

SEF STATIONS: ${clipJson(stationPayload, 28000)}
SCHEMA FIELD SUMMARIES (ids for linking only - do not copy ids into claim text): ${clipJson(fieldSummaries, 18000)}
EVIDENCE FILES / QUALITY (samples / truncated for large vaults): ${clipJson(fileSummaries, 36000)}

Return ONLY raw JSON:
{
  "softEvidence": [
    {
      "id": "soft_01",
      "stationId": "01",
      "text": "plain English claim or narrative point",
      "source": "sef_narrative",
      "backed_by_upload": false,
      "benchmarkEligible": false
    }
  ],
  "claims": [
    {
      "id": "claim_01",
      "stationId": "01",
      "claim": "plain English discrete claim",
      "status": "supported|partial|unsupported|contradicted|soft_only",
      "linkedFileNames": [],
      "linkedFieldLabels": ["plain English label of what field supports, not raw schema keys"],
      "notes": "plain English"
    }
  ],
  "metrics": {
    "totalClaims": 0,
    "supported": 0,
    "partial": 0,
    "unsupported": 0,
    "contradicted": 0,
    "softOnly": 0,
    "supportRatePct": 0,
    "byStation": {}
  }
}

Rules:
- Prefer 2-6 material claims per station that has narrative; skip empty stations.
- soft_only / backed_by_upload false when only SEF narrative supports it.
- status supported only when schema extract or uploaded non-photo file clearly backs it.
- claim text and notes MUST be plain English for accreditors - never raw schema keys like demo.total_roll.
- No em dashes. No markdown.
`.trim();
}

/** Non-AI fallback so draft generation is never blocked by mapper size/API failures. */
function heuristicClaimsFromStations(stations) {
  const softEvidence = [];
  const claims = [];
  Object.entries(stations || {}).forEach(([stationId, s]) => {
    const narrative = String(s?.narrative || '').trim();
    if (!narrative) return;
    softEvidence.push({
      id: `soft_${stationId}`,
      stationId,
      text: clipText(narrative, 320),
      source: 'sef_narrative',
      backed_by_upload: false,
      benchmarkEligible: false,
    });
    claims.push({
      id: `claim_${stationId}`,
      stationId,
      claim: clipText(narrative, 280),
      status: 'soft_only',
      linkedFileNames: [],
      linkedFieldLabels: [],
      notes: 'Heuristic claim (AI mapper skipped or failed); treat as narrative-only until remapped.',
    });
  });
  const softOnly = claims.length;
  return {
    softEvidence,
    claimsMap: claims,
    claimMetrics: {
      totalClaims: claims.length,
      supported: 0,
      partial: 0,
      unsupported: 0,
      contradicted: 0,
      softOnly,
      supportRatePct: 0,
      byStation: {},
      heuristic: true,
    },
  };
}

async function persistClaims(sid, softEvidence, claims, claimMetrics) {
  await setDoc(
    schoolSchemaRef(sid),
    {
      schoolId: sid,
      pipeline: AI_PIPELINE_SCHEMA_V2,
      softEvidence,
      claimsMap: claims,
      claimMetrics,
      claimsUpdatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Build soft evidence + SEF claims map from stations, schema, and evidence job quality.
 * Stores on schoolSchemas/{sid}. Additive; does not touch legacy/v1.
 * Prompt payloads are compacted to stay under the server Gemini character limit.
 */
export async function buildSoftEvidenceAndClaimsMap(schoolId) {
  const sid = String(schoolId || '').trim();
  if (!sid) throw new Error('schoolId required');

  const [auditSnap, schemaSnap, job] = await Promise.all([
    getDoc(doc(db, 'audits', sid)),
    getDoc(schoolSchemaRef(sid)),
    getEvidenceAnalysisJob(sid),
  ]);

  const stations = auditSnap.exists() ? auditSnap.data()?.stations || {} : {};
  const schema = schemaSnap.exists() ? schemaSnap.data() : { fields: {} };
  const fields = schema.fields || {};
  const files = Array.isArray(job?.files) ? job.files : [];

  const tiers = [
    { narrativeMax: 1800, valueMax: 220, detailMax: 180, maxFields: 80, maxFiles: 40 },
    { narrativeMax: 900, valueMax: 140, detailMax: 120, maxFields: 50, maxFiles: 25 },
    { narrativeMax: 450, valueMax: 100, detailMax: 80, maxFields: 30, maxFiles: 15 },
    { narrativeMax: 250, valueMax: 80, detailMax: 60, maxFields: 20, maxFiles: 10 },
  ];

  let prompt = '';
  for (const tier of tiers) {
    prompt = buildMapperPrompt({
      stationPayload: compactStations(stations, tier.narrativeMax),
      fieldSummaries: compactFields(fields, tier.valueMax, tier.maxFields),
      fileSummaries: compactFiles(files, tier.detailMax, tier.maxFiles),
    });
    if (prompt.length <= PROMPT_BUDGET) break;
  }

  if (prompt.length > PROMPT_BUDGET) {
    const fallback = heuristicClaimsFromStations(stations);
    await persistClaims(sid, fallback.softEvidence, fallback.claimsMap, fallback.claimMetrics);
    return fallback;
  }

  let result;
  try {
    result = await geminiGenerateJson(prompt);
  } catch (err) {
    const msg = String(err?.message || err || '');
    if (/prompt too long/i.test(msg) || /too large/i.test(msg)) {
      const fallback = heuristicClaimsFromStations(stations);
      await persistClaims(sid, fallback.softEvidence, fallback.claimsMap, fallback.claimMetrics);
      return fallback;
    }
    throw err;
  }

  const softEvidence = Array.isArray(result.softEvidence) ? result.softEvidence : [];
  const claims = Array.isArray(result.claims) ? result.claims : [];
  const metrics = result.metrics && typeof result.metrics === 'object' ? result.metrics : {};

  const total = claims.length || Number(metrics.totalClaims) || 0;
  const supported = claims.filter((c) => c.status === 'supported').length;
  const partial = claims.filter((c) => c.status === 'partial').length;
  const softOnly = claims.filter((c) => c.status === 'soft_only').length;
  const unsupported = claims.filter((c) => c.status === 'unsupported').length;
  const contradicted = claims.filter((c) => c.status === 'contradicted').length;
  const material = Math.max(total - softOnly, 1);
  const supportRatePct =
    typeof metrics.supportRatePct === 'number'
      ? metrics.supportRatePct
      : Math.round(((supported + partial * 0.5) / material) * 100);

  const claimMetrics = {
    totalClaims: total,
    supported,
    partial,
    unsupported,
    contradicted,
    softOnly,
    supportRatePct,
    byStation: metrics.byStation || {},
  };

  await persistClaims(sid, softEvidence, claims, claimMetrics);
  return { softEvidence, claimsMap: claims, claimMetrics };
}

export async function ensureSoftEvidenceAndClaims(schoolId) {
  const snap = await getDoc(schoolSchemaRef(schoolId));
  const data = snap.exists() ? snap.data() : {};
  if (Array.isArray(data.claimsMap) && data.claimsMap.length > 0 && data.claimMetrics) {
    return {
      softEvidence: data.softEvidence || [],
      claimsMap: data.claimsMap,
      claimMetrics: data.claimMetrics,
    };
  }
  return buildSoftEvidenceAndClaimsMap(schoolId);
}
